import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { X, Filter } from "lucide-react";

const PAGE = 30;

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]); // unfiltered, to derive available options
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PAGE);
  const [settings, setSettings] = useState(null);

  const diameter = sp.get("size") || "";
  const modeli = sp.get("size_code") || "";

  useEffect(() => { api.get("/settings").then(r => setSettings(r.data)).catch(() => setSettings({})); }, []);

  // Load unfiltered once to compute available dropdown values
  useEffect(() => {
    api.get("/products?limit=5000").then(r => setAllItems(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    setVisible(PAGE);
    const params = new URLSearchParams();
    if (diameter) params.set("size", diameter);
    if (modeli) params.set("size_code", modeli);
    params.set("limit", "5000");
    api.get(`/products?${params.toString()}`).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [diameter, modeli]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next);
  };
  const clearAll = () => setSp(new URLSearchParams());

  const shown = useMemo(() => items.slice(0, visible), [items, visible]);
  const hasMore = visible < items.length;

  // Build sets of sizes / size_codes that have at least one product.
  // When the other filter is active, intersect with that filtered set so each
  // dropdown shows only values that still return results in combination.
  const { usedDiameters, usedModels } = useMemo(() => {
    const forDiameters = modeli
      ? allItems.filter(p => (p.size_code || "") === modeli)
      : allItems;
    const forModels = diameter
      ? allItems.filter(p => (p.size || "") === diameter)
      : allItems;
    const d = new Set();
    const m = new Set();
    forDiameters.forEach(p => { if (p.size) d.add(p.size); });
    forModels.forEach(p => { if (p.size_code) m.add(p.size_code); });
    return { usedDiameters: d, usedModels: m };
  }, [allItems, diameter, modeli]);

  const diameterGroups = settings?.diameters || {};
  const modelGroups = settings?.size_codes || {};

  const renderGroups = (groups, usedSet, currentValue) =>
    Object.entries(groups).map(([group, values]) => {
      const filtered = (values || []).filter(v => usedSet.has(v) || v === currentValue);
      if (filtered.length === 0) return null;
      return (
        <optgroup key={group} label={group}>
          {filtered.map(v => <option key={v} value={v}>{v}</option>)}
        </optgroup>
      );
    });

  const diameterOpts = renderGroups(diameterGroups, usedDiameters, diameter).filter(Boolean);
  const modelOpts = renderGroups(modelGroups, usedModels, modeli).filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-3xl sm:text-4xl uppercase mb-4 flex items-center gap-2">
        <Filter className="text-[#FFD600]" size={28} /> Filtro produktet
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-[11px] uppercase text-neutral-400 tracking-widest font-bold">Diametri</label>
          <select
            value={diameter}
            onChange={e => setFilter("size", e.target.value)}
            data-testid="filter-diameter"
            disabled={diameterOpts.length === 0}
            className="w-full mt-1 bg-[#131E30] border border-[#1F2B44] focus:border-[#FFD600] px-3 py-3 outline-none text-base disabled:opacity-50"
          >
            <option value="">— Të gjitha diametrat —</option>
            {diameterOpts}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase text-neutral-400 tracking-widest font-bold">Modeli / Kodi</label>
          <select
            value={modeli}
            onChange={e => setFilter("size_code", e.target.value)}
            data-testid="filter-model"
            disabled={modelOpts.length === 0}
            className="w-full mt-1 bg-[#131E30] border border-[#1F2B44] focus:border-[#FFD600] px-3 py-3 outline-none text-base disabled:opacity-50"
          >
            <option value="">— Të gjithë modelet —</option>
            {modelOpts}
          </select>
        </div>
      </div>

      {(diameter || modeli) && (
        <div className="flex items-center flex-wrap gap-2 mb-4">
          {diameter && (
            <button onClick={() => setFilter("size", "")} data-testid="chip-diameter"
              className="inline-flex items-center gap-1 bg-[#FFD600] text-black px-2.5 py-1 text-xs uppercase font-bold">
              Diametri: {diameter} <X size={12} />
            </button>
          )}
          {modeli && (
            <button onClick={() => setFilter("size_code", "")} data-testid="chip-model"
              className="inline-flex items-center gap-1 bg-[#FFD600] text-black px-2.5 py-1 text-xs uppercase font-bold">
              Modeli: {modeli} <X size={12} />
            </button>
          )}
          <button onClick={clearAll} data-testid="clear-filters"
            className="text-xs uppercase text-neutral-400 hover:text-[#FFD600] underline">
            Pastro të gjitha
          </button>
        </div>
      )}

      <div className="text-sm text-neutral-400 mb-4" data-testid="result-count">
        {loading ? "Duke ngarkuar..." : `${items.length} rezultate`}
      </div>

      {!loading && items.length === 0 && (
        <div className="text-center text-neutral-400 py-16 hg-card">Nuk u gjet asnjë produkt me këto filtra.</div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {shown.map(p => <ProductCard key={p.id} p={p} />)}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setVisible(v => v + PAGE)}
            data-testid="load-more"
            className="hg-btn-primary px-8 py-3 uppercase text-xs tracking-widest font-bold"
          >
            Vazhdo me më shumë produkte ({items.length - visible} mbetur)
          </button>
        </div>
      )}
    </div>
  );
}
